import { useEffect, useRef } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Quiz from "./pages/Quiz";
import Leaderboard from "./pages/Leaderboard";
import StudyPlanList from "./pages/StudyPlanList";
import StudyPlanCreate from "./pages/StudyPlanCreate";
import StudyPlanDetail from "./pages/StudyPlanDetail";
import Review from "./pages/Review";
import Badges from "./pages/Badges";
import ExamCreate from "./pages/ExamCreate";
import ExamDetail from "./pages/ExamDetail";
import Flashcards from "./pages/Flashcards";
import Theory from "./pages/Theory";
import Formulas from "./pages/Formulas";
import Simulation from "./pages/Simulation";
import QuickReview from "./pages/QuickReview";
import Progress from "./pages/Progress";
import Compare from "./pages/Compare";
import api from "./lib/api";

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const params = new URLSearchParams(window.location.hash.replace("#", ""));
    const sid = params.get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/session", { session_id: sid });
        setUser(data.user);
      } catch {}
      window.history.replaceState(null, "", "/dashboard");
      navigate("/dashboard", { replace: true });
    })();
  }, [navigate, setUser]);
  return <div className="space-bg min-h-screen flex items-center justify-center text-white/70">Accesso in corso...</div>;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="space-bg min-h-screen flex items-center justify-center text-white/60">Caricamento...</div>;
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/quiz/:levelId" element={<Protected><Quiz /></Protected>} />
      <Route path="/leaderboard" element={<Protected><Leaderboard /></Protected>} />
      <Route path="/plans" element={<Protected><StudyPlanList /></Protected>} />
      <Route path="/plan/new" element={<Protected><StudyPlanCreate /></Protected>} />
      <Route path="/plan/:planId" element={<Protected><StudyPlanDetail /></Protected>} />
      <Route path="/review" element={<Protected><Review /></Protected>} />
      <Route path="/badges" element={<Protected><Badges /></Protected>} />
      <Route path="/exams" element={<Navigate to="/dashboard" replace />} />
      <Route path="/exam/new" element={<Protected><ExamCreate /></Protected>} />
      <Route path="/exam/:examId" element={<Protected><ExamDetail /></Protected>} />
      <Route path="/memory/:examId/:topicId" element={<Protected><Flashcards /></Protected>} />
      <Route path="/theory/:examId/:topicId" element={<Protected><Theory /></Protected>} />
      <Route path="/formulas/:examId/:topicId" element={<Protected><Formulas /></Protected>} />
      <Route path="/simulation/:examId" element={<Protected><Simulation /></Protected>} />
      <Route path="/quick/:examId" element={<Protected><QuickReview /></Protected>} />
      <Route path="/compare/:examId" element={<Protected><Compare /></Protected>} />
      <Route path="/progress" element={<Protected><Progress /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <LanguageProvider>
          <BrowserRouter>
            <Toaster position="top-center" theme="dark" richColors />
            <AppRouter />
          </BrowserRouter>
        </LanguageProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
